import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    getCodesByCollection,
    exportIncomeCodeCollection,
} from '../../../data/api/incomes/IncomeRoutes';
import useIndex from '../../../hooks/useIndex';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import validErrors from '../../../hooks/validErrors';

const GameCodesTable = ({ collectionUuid, nameDoc }) => {
    const { token } = useIndex();

    const [page, setPage] = useState(1);
    const [load, setLoad] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [codes, setCodes] = useState([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        loadData(collectionUuid);
    }, [page, rowsPerPage]);

    const loadData = async (uuid) => {
        setLoad(false);

        try {
            const { data } = await getCodesByCollection(token, uuid, rowsPerPage, page);

            if (data?.success) {
                setCodes(data);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(true);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(1);
    };

    const exportCollection = async () => {
        setExporting(true);

        try {
            const response = await exportIncomeCodeCollection(token, nameDoc, collectionUuid);

            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'income_collection.xlsx';

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch?.length === 2) fileName = fileNameMatch[1];
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            window.URL.revokeObjectURL(url);
            link.remove();
        } catch (error) {
            validErrors(error);
        } finally {
            setExporting(false);
        }
    };

    const getStatusStyle = (status) => {
        const value = String(status ?? '').toLowerCase();

        if (value.includes('usado') || value.includes('utilizado')) {
            return {
                label: status,
                color: '#8a5a00',
                background: 'rgba(214, 170, 58, 0.18)',
                border: 'rgba(214, 170, 58, 0.4)',
            };
        }

        if (value.includes('activo') || value.includes('disponible')) {
            return {
                label: status,
                color: '#0f5c36',
                background: 'rgba(46, 125, 50, 0.12)',
                border: 'rgba(46, 125, 50, 0.25)',
            };
        }

        if (value.includes('vencido') || value.includes('inactivo')) {
            return {
                label: status,
                color: '#9b1c1c',
                background: 'rgba(211, 47, 47, 0.10)',
                border: 'rgba(211, 47, 47, 0.25)',
            };
        }

        return {
            label: status ?? '-',
            color: '#10285f',
            background: 'rgba(16, 40, 95, 0.08)',
            border: 'rgba(16, 40, 95, 0.14)',
        };
    };

    return (
        <TableWrapper>
            {load ? (
                <>
                    <HeaderActions>
                        <Box>
                            <Typography className="table-title">
                                Códigos de la colección
                            </Typography>

                            <Typography className="table-subtitle">
                                Consulta, exporta y revisa el estado de los códigos generados.
                            </Typography>
                        </Box>

                        <Box className="actions">
                            <LoadingButton
                                onClick={exportCollection}
                                loading={exporting}
                                loadingPosition="start"
                                startIcon={<CloudDownloadIcon />}
                                variant="contained"
                                sx={{
                                    borderRadius: '14px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                Excel
                            </LoadingButton>

                            <Button
                                variant="outlined"
                                startIcon={<PictureAsPdfIcon />}
                                sx={{
                                    borderRadius: '14px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    color: '#10285f',
                                    borderColor: 'rgba(214, 170, 58, 0.75)',
                                    background: '#fff',
                                    '&:hover': {
                                        borderColor: '#d6aa3a',
                                        background: 'rgba(214, 170, 58, 0.12)',
                                    },
                                }}
                            >
                                PDF
                            </Button>
                        </Box>
                    </HeaderActions>

                    <TableCard>
                        <TableContainer className="table-container">
                            <Table stickyHeader aria-label="result table">
                                <TableHead>
                                    <TableRow>
                                        <StyledHeadCell>ID</StyledHeadCell>
                                        <StyledHeadCell>Código</StyledHeadCell>
                                        <StyledHeadCell>Estado</StyledHeadCell>
                                        <StyledHeadCell>Usuario</StyledHeadCell>
                                        <StyledHeadCell>Vigencia</StyledHeadCell>
                                        <StyledHeadCell>Asignado</StyledHeadCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {codes?.data?.length > 0 ? (
                                        codes.data.map((item) => {
                                            const statusStyle = getStatusStyle(item?.status);

                                            return (
                                                <StyledTableRow hover tabIndex={-1} key={item?.id}>
                                                    <StyledBodyCell>
                                                        <Chip
                                                            label={`#${item?.id}`}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 900,
                                                                color: '#10285f',
                                                                background: 'rgba(16, 40, 95, 0.08)',
                                                            }}
                                                        />
                                                    </StyledBodyCell>

                                                    <StyledBodyCell>
                                                        <Typography className="code-text">
                                                            {item?.code}
                                                        </Typography>
                                                    </StyledBodyCell>

                                                    <StyledBodyCell>
                                                        <Chip
                                                            label={statusStyle.label}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 800,
                                                                color: statusStyle.color,
                                                                background: statusStyle.background,
                                                                border: `1px solid ${statusStyle.border}`,
                                                            }}
                                                        />
                                                    </StyledBodyCell>

                                                    <StyledBodyCell>{item?.user ?? '-'}</StyledBodyCell>
                                                    <StyledBodyCell>{item?.validity ?? '-'}</StyledBodyCell>
                                                    <StyledBodyCell>{item?.updated_at ?? '-'}</StyledBodyCell>
                                                </StyledTableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <EmptyState>
                                                    <i className="fa-regular fa-folder-open"></i>
                                                    <Typography className="empty-title">
                                                        No hay códigos
                                                    </Typography>
                                                    <Typography className="empty-text">
                                                        Esta colección no tiene códigos registrados.
                                                    </Typography>
                                                </EmptyState>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <FooterPagination>
                            <Box className="rows-control">
                                <Typography className="rows-label">Filas:</Typography>

                                <FormControl size="small">
                                    <Select
                                        value={rowsPerPage}
                                        onChange={handleChangeRowsPerPage}
                                        sx={{
                                            borderRadius: '12px',
                                            color: '#10285f',
                                            fontWeight: 800,
                                            background: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(15, 34, 77, 0.16)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(214, 170, 58, 0.7)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#d6aa3a',
                                            },
                                        }}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Pagination
                                count={codes?.meta?.last_page ?? 1}
                                page={page}
                                showFirstButton
                                showLastButton
                                onChange={handleChangePage}
                                size="small"
                                shape="rounded"
                            />
                        </FooterPagination>
                    </TableCard>
                </>
            ) : (
                <LoadingState>
                    <CircularProgress sx={{ color: '#d6aa3a' }} />
                    <Typography>Cargando códigos...</Typography>
                </LoadingState>
            )}
        </TableWrapper>
    );
};

export default GameCodesTable;

const TableWrapper = styled.div`
    width: 100%;
`;

const HeaderActions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;

    .table-title {
        color: #10285f;
        font-size: 1.1rem;
        font-weight: 900;
        margin: 0;
    }

    .table-subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
        font-weight: 600;
        margin-top: 0.15rem;
    }

    .actions {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        flex-wrap: wrap;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;

        .actions {
            width: 100%;

            button {
                flex: 1;
            }
        }
    }
`;

const TableCard = styled.div`
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    overflow: hidden;
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.07);

    .table-container {
        max-height: 500px;
        max-width: 100%;
    }
`;

const StyledHeadCell = styled(TableCell)`
    background: #10285f !important;
    color: #f8f4e8 !important;
    font-weight: 900 !important;
    font-size: 0.82rem !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #d6aa3a !important;
    white-space: nowrap;
`;

const StyledBodyCell = styled(TableCell)`
    color: #10285f !important;
    font-size: 0.88rem !important;
    font-weight: 600 !important;
    border-bottom: 1px solid rgba(15, 34, 77, 0.08) !important;

    .code-text {
        font-family: monospace;
        font-weight: 900;
        color: #10285f;
        background: rgba(16, 40, 95, 0.06);
        border: 1px solid rgba(16, 40, 95, 0.1);
        border-radius: 10px;
        display: inline-block;
        padding: 0.25rem 0.55rem;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const StyledTableRow = styled(TableRow)`
    transition: 0.2s ease;

    &:hover {
        background: rgba(214, 170, 58, 0.08) !important;
    }
`;

const FooterPagination = styled.div`
    min-height: 58px;
    padding: 0.7rem 1rem;
    border-top: 1px solid rgba(15, 34, 77, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    .rows-control {
        display: flex;
        align-items: center;
        gap: 0.6rem;
    }

    .rows-label {
        color: #10285f;
        font-weight: 900;
        font-size: 0.9rem;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }
`;

const LoadingState = styled.div`
    min-height: 260px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const EmptyState = styled.div`
    min-height: 180px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    i {
        font-size: 2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.1rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
    }
`;